import { t } from '@lingui/macro';
import { assertUnreachable, isPresent, transEnum } from '@luminovo/commons';
import {
    CustomPartOfferStatuses,
    PriceRadarStatusKind,
    PropertyErrorKind,
    PropertyErrorKindEnum,
} from '@luminovo/http-client';
import {
    apiNotSetupErrorTranslations,
    customLabelTranslations,
    panelErrorTranslations,
    priceRadarErrorsTranslations,
} from '../../../../../resources/pcb/i18n';

export type PriceRadarViolation =
    | {
          name: string;
          message: string;
          type: 'capability';
      }
    | {
          message: string;
          type: 'price_break';
      }
    | {
          message: string;
          type: 'error';
      };

export const convertOfferErrorToText = (offer: CustomPartOfferStatuses): PriceRadarViolation[] | undefined => {
    switch (offer.type) {
        case PriceRadarStatusKind.OffersAvailable:
            return undefined;

        case PriceRadarStatusKind.NoOffersReceived:
            return [
                {
                    message: t`No offers received`,
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.NeedsRefreshForOffers:
            return [
                {
                    message: t`Refresh offers to get prices for this supplier`,
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.MissingCredentials:
            return [
                {
                    message: t`Missing credentials`,
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.InvalidCredentials:
            return [
                {
                    message: t`Invalid credentials`,
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.Pending:
            return [
                {
                    message: t`Offer is pending`,
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.InvalidSpecification:
            return offer.errors.map((error) => {
                const propertyName = transEnum(error.name, priceRadarErrorsTranslations);

                if (isPresent(error.kind)) {
                    return {
                        name: propertyName,
                        message: getPropertyErrorTranslation(propertyName, error.error, error.kind),
                        type: 'capability',
                    };
                }

                return {
                    name: propertyName,
                    message: error.error,
                    type: 'capability',
                };
            });

        case PriceRadarStatusKind.PanelError:
            return [
                {
                    message: transEnum(offer.error.type, panelErrorTranslations),
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.SupplierApiError:
            return [
                {
                    message: t`We're having issues fetching instant offers from this supplier. Please try again later. If the issue persists, contact support.`,
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.ApiNotSetUp:
            return [
                {
                    message: transEnum(offer.error.type, apiNotSetupErrorTranslations),
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.CustomStackup:
            return [
                {
                    message: t`PCBs with custom stackups require manual quote requests`,
                    type: 'error',
                },
            ];

        case PriceRadarStatusKind.StackratePricingBreaks:
            return offer.pricing_breaks
                .filter((error) => {
                    if (isPresent(error.fields)) {
                        return !error.fields.some((variable) => variable === 'order_delivery');
                    }
                    return true;
                })
                .map((error) => ({
                    message: error.message,
                    type: 'price_break',
                }));

        default:
            assertUnreachable(offer);
    }
};

function getPropertyErrorTranslation(propertyName: string, message: string, type: PropertyErrorKind): string {
    switch (type.type) {
        case PropertyErrorKindEnum.Plain:
            return message;
        case PropertyErrorKindEnum.Empty:
            return t`The value for ${propertyName} is empty`;
        case PropertyErrorKindEnum.NotInSet:
        case PropertyErrorKindEnum.NotSupported:
        case PropertyErrorKindEnum.ConversionError:
            return t`The value for ${propertyName} is not supported`;
        case PropertyErrorKindEnum.CustomLabel:
            return transEnum(type.label, customLabelTranslations);
        case PropertyErrorKindEnum.BelowMin:
            return t`The value of ${type.value} is below the supported minimum of ${type.min}${type.unit}`;
        case PropertyErrorKindEnum.AboveMax:
            return t`The value of ${type.value} is above the supported maximum of ${type.max}${type.unit}`;
        default:
            assertUnreachable(type);
    }
}
